import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "dtp__container" }
const _hoisted_2 = { class: "dtp__row" }
const _hoisted_3 = { class: "dtp__grid-container" }
const _hoisted_4 = { class: "dtp__middle-slot" }
const _hoisted_5 = {
  key: 0,
  class: "dtp__ampm"
}
const _hoisted_6 = { class: "dtp__bottom-content" }
const _hoisted_7 = {
  key: 0,
  class: "dtp__debug"
}

import { ref, watch, computed } from 'vue';
import TapToInput from './TapToInput.vue';


export interface Props {
  debug?: boolean;
  useAmpm?: boolean;
  editableTime?: boolean;
}

type Unit = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';


export default /*@__PURE__*/_defineComponent({
  __name: 'DateTimePicker',
  props: /*@__PURE__*/_mergeModels({
    debug: { type: Boolean, default: false },
    useAmpm: { type: Boolean, default: true },
    editableTime: { type: Boolean, default: true }
  }, {
    "modelValue": {default: new Date('2027-01-01 12:00:00 UTC')},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const props = __props;


const date = _useModel(__props, "modelValue");

const year = ref(date.value.getFullYear());
const month = ref(date.value.getMonth()+1);
const day = ref(date.value.getDate());
const hour = ref(date.value.getHours());
const minute = ref(date.value.getMinutes());
const second = ref(date.value.getSeconds());

// const ampm = ref(hour.value >= 12);


const isAm = computed({
  get: () => hour.value < 12,
  set: (value) => {
    if (value) {
      for (let i = 0; i < 12; i++) {
        decrement('hour');
      }
    } else {
      for (let i = 0; i < 12; i++) {
        increment('hour');
      }
    }
  }
});

// watch(ampm, (value) => { isAm.value = value;});

const values = {
  year: year,
  month: month,
  day: day,
  hour: hour,
  minute: minute,
  second: second,
};

const limits = computed(() => ({
  year: { min: 1, max: Infinity },
  month: { min: 1, max: 12 },
  day: { min: 1, max: _daysInMonth(month.value, year.value) },
  hour: { min: 0, max: 23 },
  minute: { min: 0, max: 59 },
  second: { min: 0, max: 59 },
}));

const units: Unit[] = ['second', 'minute', 'hour', 'day', 'month', 'year'];

function changeValue(unit: Unit, increment: boolean) {
  const limit = limits.value[unit].max;
  const min = limits.value[unit].min;

  if (increment) {
    if (values[unit].value < limit) {
      values[unit].value++;
    } else {
      values[unit].value = min;
      const nextUnit = units[units.indexOf(unit) + 1];
      if (nextUnit) {
        changeValue(nextUnit, increment);
      } else if (unit === 'hour' && props.useAmpm) {
        // Toggle AM/PM when hour goes from 11 to 12
        isAm.value = !isAm.value;
      }
    }
  } else {
    if (values[unit].value > min) {
      values[unit].value--;
    } else {
      values[unit].value = limit;
      const prevUnit = units[units.indexOf(unit) + 1];
      if (prevUnit) {
        changeValue(prevUnit, increment);
      } else if (unit === 'hour' && props.useAmpm) {
        // Toggle AM/PM when hour goes from 12 to 11
        isAm.value = !isAm.value;
      }
    }
  }

  // Ensure hour is always between 0 and 23
  if (unit === 'hour') {
    hour.value = hour.value % 24;
  }
}

const displayHour = computed({
  get() {
    if (props.useAmpm) {
      const h = hour.value % 12;
      return h === 0 ? 12 : h;
    } else {
      return hour.value;
    }
  },
  set(value: number) {
    if (props.useAmpm) {
      if (value === 12) {
        hour.value = isAm.value ? 0 : 12;
      } else {
        hour.value = isAm.value ? value : value + 12;
      }
    } else {
      hour.value = value;
    }
  }
});

function increment(value: Unit) {
  changeValue(value, true);
}

function decrement(value: Unit) {
  changeValue(value, false);
}

function _daysInMonth(month: number, year: number) {
  return new Date(year, month, 0).getDate();
}


function utcDate(date: Date) {
  // utc date string
  return date.toISOString(); 
}

function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

// update the date v-model
watch([year, month, day, hour, minute, second], () => {
  date.value = new Date(year.value, month.value-1, day.value, hour.value, minute.value, second.value);
});

// watcher to fix leap years
watch([year, month, day, hour, minute, second], () => {
  if (month.value === 2 && day.value > _daysInMonth(month.value, year.value)) {
    day.value = _daysInMonth(month.value, year.value);
  }
});
// after we have mounted make the up and down buttons the same width
// keep this up to date with the model value
watch(date, () => {
  year.value = date.value.getFullYear();
  month.value = date.value.getMonth()+1;
  day.value = date.value.getDate();
  hour.value = date.value.getHours();
  minute.value = date.value.getMinutes();
  second.value = date.value.getSeconds();
});


return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          id: "dtp__year-up",
          class: "dtp__grid-item",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (increment('year')))
        }, [
          _cache[21] || (_cache[21] = _createElementVNode("span", { class: "dtp__button-label" }, "yr", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[20] || (_cache[20] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__year",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.year.min,
          max: limits.value.year.max,
          modelValue: year.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((year).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__year-up",
          class: "dtp__grid-item",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (decrement('year')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[22] || (_cache[22] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ]),
        _cache[40] || (_cache[40] = _createElementVNode("span", null, null, -1)),
        _cache[41] || (_cache[41] = _createElementVNode("span", null, "-", -1)),
        _cache[42] || (_cache[42] = _createElementVNode("span", null, null, -1)),
        _createElementVNode("button", {
          id: "dtp__month-up",
          class: "dtp__grid-item",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (increment('month')))
        }, [
          _cache[24] || (_cache[24] = _createElementVNode("span", { class: "dtp__button-label" }, "mo", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[23] || (_cache[23] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__month",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.month.min,
          max: limits.value.month.max,
          pad2: "",
          modelValue: month.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((month).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__month-up",
          class: "dtp__grid-item",
          onClick: _cache[5] || (_cache[5] = ($event: any) => (decrement('month')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[25] || (_cache[25] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ]),
        _cache[43] || (_cache[43] = _createElementVNode("span", null, null, -1)),
        _cache[44] || (_cache[44] = _createElementVNode("span", null, "-", -1)),
        _renderSlot(_ctx.$slots, "bottom-middle", {}, () => [
          _cache[26] || (_cache[26] = _createElementVNode("span", null, null, -1))
        ]),
        _createElementVNode("button", {
          id: "dtp__day-up",
          class: "dtp__grid-item",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (increment('day')))
        }, [
          _cache[28] || (_cache[28] = _createElementVNode("span", { class: "dtp__button-label" }, "day", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[27] || (_cache[27] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__day",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.day.min,
          max: limits.value.day.max,
          pad2: "",
          modelValue: day.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((day).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__day-up",
          class: "dtp__grid-item",
          onClick: _cache[8] || (_cache[8] = ($event: any) => (decrement('day')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[29] || (_cache[29] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ]),
        _renderSlot(_ctx.$slots, "top-middle", {}, () => [
          _cache[30] || (_cache[30] = _createElementVNode("span", null, null, -1))
        ]),
        _createElementVNode("span", _hoisted_4, [
          _renderSlot(_ctx.$slots, "center-middle")
        ]),
        _cache[45] || (_cache[45] = _createElementVNode("span", null, null, -1)),
        _createElementVNode("button", {
          id: "dtp__hour-up",
          class: "dtp__grid-item",
          onClick: _cache[9] || (_cache[9] = ($event: any) => (increment('hour')))
        }, [
          _cache[32] || (_cache[32] = _createElementVNode("span", { class: "dtp__button-label" }, "hr", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[31] || (_cache[31] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__hour",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.hour.min,
          max: limits.value.hour.max,
          pad2: "",
          modelValue: displayHour.value,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((displayHour).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__hour-up",
          class: "dtp__grid-item",
          onClick: _cache[11] || (_cache[11] = ($event: any) => (decrement('hour')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[33] || (_cache[33] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ]),
        _cache[46] || (_cache[46] = _createElementVNode("span", null, null, -1)),
        _cache[47] || (_cache[47] = _createElementVNode("span", null, ":", -1)),
        _cache[48] || (_cache[48] = _createElementVNode("span", null, null, -1)),
        _createElementVNode("button", {
          id: "dtp__minute-up",
          class: "dtp__grid-item",
          onClick: _cache[12] || (_cache[12] = ($event: any) => (increment('minute')))
        }, [
          _cache[35] || (_cache[35] = _createElementVNode("span", { class: "dtp__button-label" }, "min", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[34] || (_cache[34] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__minute",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.minute.min,
          max: limits.value.minute.max,
          pad2: "",
          modelValue: minute.value,
          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((minute).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__minute-up",
          class: "dtp__grid-item",
          onClick: _cache[14] || (_cache[14] = ($event: any) => (decrement('minute')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[36] || (_cache[36] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ]),
        _cache[49] || (_cache[49] = _createElementVNode("span", null, null, -1)),
        _cache[50] || (_cache[50] = _createElementVNode("span", null, ":", -1)),
        _cache[51] || (_cache[51] = _createElementVNode("span", null, null, -1)),
        _createElementVNode("button", {
          id: "dtp__second-up",
          class: "dtp__grid-item",
          onClick: _cache[15] || (_cache[15] = ($event: any) => (increment('second')))
        }, [
          _cache[38] || (_cache[38] = _createElementVNode("span", { class: "dtp__button-label" }, "sec", -1)),
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[37] || (_cache[37] = [
              _createTextVNode("mdi-menu-up")
            ])),
            _: 1
          })
        ]),
        _createVNode(TapToInput, {
          editable: props.editableTime,
          id: "dtp__second",
          class: "dtp__grid-item dtp__time_part",
          min: limits.value.second.min,
          max: limits.value.second.max,
          pad2: "",
          modelValue: second.value,
          "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((second).value = $event))
        }, null, 8, ["editable", "min", "max", "modelValue"]),
        _createElementVNode("button", {
          id: "dtp__second-up",
          class: "dtp__grid-item",
          onClick: _cache[17] || (_cache[17] = ($event: any) => (decrement('second')))
        }, [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => _cache[39] || (_cache[39] = [
              _createTextVNode("mdi-menu-down")
            ])),
            _: 1
          })
        ])
      ]),
      (props.useAmpm)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("button", {
              name: "set-am",
              class: _normalizeClass(["dtp__ampm-button", "dtp__ampm-am", { "dtp__ampm-active": isAm.value }]),
              onClick: _cache[18] || (_cache[18] = ($event: any) => (isAm.value = true)),
              "aria-label": "Set AM"
            }, "AM", 2),
            _createElementVNode("button", {
              name: "set-pm",
              class: _normalizeClass(["dtp__ampm-button", "dtp__ampm-pm", { "dtp__ampm-active": !isAm.value }]),
              onClick: _cache[19] || (_cache[19] = ($event: any) => (isAm.value = false)),
              "aria-label": "Set PM"
            }, "PM", 2)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_6, [
      _renderSlot(_ctx.$slots, "default")
    ]),
    (props.debug)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("span", null, "Local: " + _toDisplayString(year.value) + "-" + _toDisplayString(pad2(month.value)) + "-" + _toDisplayString(pad2(day.value)) + " " + _toDisplayString(pad2(hour.value)) + ":" + _toDisplayString(pad2(minute.value)) + ":" + _toDisplayString(pad2(second.value)), 1),
          _createElementVNode("span", null, "UTC: " + _toDisplayString(utcDate(date.value)), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})